import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import moment from "moment";
import Navbar from "../../Navbar";
import { logEventos } from "../../../services/services";
import {
  getChequeById,
  getCheques,
  getChequesResumen,
  getMenuesUsuario,
} from "./ApiServices";
import ModalImportarCheques from "./ModalImportarCheques";
import ModalImportarAceptados from "./ModalImportarAceptados";
import ModalRevisionCC from "./ModalRevisionCC";
import ModalBajaCheque from "./ModalBajaCheque";
import ModalPasarACartera from "./ModalPasarACartera";
import ModalDestinoCheque from "./ModalDestinoCheque";
import ModalAltaRechazo from "./ModalAltaRechazo";
import { formatCuit, formatMoneda } from "../pago_proveedores/utils";
import {
  BANDEJAS,
  ESTADOS_FILTRO,
  ESTADOS_WORKFLOW,
  MENU_CHEQUES,
  ORIGEN_CHEQUE_FILTRO,
  bandejaDesdePath,
  abrevOrigenCheque,
  colorEstado,
  colorOrigenCheque,
  esChequePropio,
  esEstadoRepudio,
  idFilaCheque,
  labelEstado,
  labelOrigenCheque,
  queryOrigenCheque,
  normalizarRutaMenu,
  tituloBandeja,
} from "./cheques.constants";
import {
  MENU_CHEQUES_RECHAZOS,
  ORIGEN_RECHAZO,
  badgesRechazosDesdeResumen,
} from "./chequesRechazos.constants";
import { VISTA_ECHEQUE, rutaEcheq } from "./echeqMenu.constants";
import TabsCircuitoEcheq, {
  labelTabConBadge,
  sxTabsConBadge,
} from "./TabsCircuitoEcheq";
import {
  MENU_LISTA_NEGRA,
  rutaListaNegra,
} from "../cheques_lista_negra/listaNegra.constants";

const PAGE_SIZE = 50;

const formatearFecha = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY") : "—";
};

const filtrosIniciales = () => ({
  estado: "",
  origen_cheque: "",
  cuit_recibido: "",
  fecha_pago_desde: "",
  fecha_pago_hasta: "",
  q: "",
});

const CampoDetalle = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value ?? "—"}</Typography>
  </Grid>
);

const flagAccion = (cheque, key) => {
  const v = cheque?.acciones?.[key];
  return typeof v === "boolean" ? v : null;
};

/** Revisar CC: solo bandeja CC + menú + flag (pendiente_cc o pendiente). */
const puedeRevisarCc = (cheque, menus, bandeja) => {
  if (esChequePropio(cheque)) return false;
  if (!menus.cc || bandeja !== BANDEJAS.CC) return false;
  const flag = flagAccion(cheque, "puede_revisar_cc");
  if (flag !== null) return flag;
  return (
    cheque?.estado === ESTADOS_WORKFLOW.PENDIENTE_CC ||
    cheque?.estado === ESTADOS_WORKFLOW.PENDIENTE
  );
};

/** Repudiar: CC no acepta el eCheq (pendiente_cc | pendiente). No es rechazo bancario. */
const puedeRepudiar = (cheque, menus, bandeja) => {
  if (esChequePropio(cheque)) return false;
  if (!menus.cc || bandeja !== BANDEJAS.CC) return false;
  const flag = flagAccion(cheque, "puede_repudiar");
  if (flag !== null) return flag;
  return (
    cheque?.estado === ESTADOS_WORKFLOW.PENDIENTE_CC ||
    cheque?.estado === ESTADOS_WORKFLOW.PENDIENTE
  );
};

/** Baja: última validación, solo autorizado (aceptado). */
const puedeBaja = (cheque, menus, bandeja) => {
  if (!menus.cc || bandeja !== BANDEJAS.CC) return false;
  const flag = flagAccion(cheque, "puede_baja");
  if (flag !== null) return flag;
  return cheque?.estado === ESTADOS_WORKFLOW.ACEPTADO;
};

const puedeReabrir = (cheque, menus, bandeja) => {
  if (esChequePropio(cheque)) return false;
  if (!menus.cc || bandeja !== BANDEJAS.CC) return false;
  const flag = flagAccion(cheque, "puede_reabrir");
  if (flag !== null) return flag;
  return (
    esEstadoRepudio(cheque?.estado) || cheque?.estado === ESTADOS_WORKFLOW.BAJA
  );
};

/** Fallback de migración/emergencia. No se muestra en el listado (happy path = Excel). */
const puedePasarCartera = (cheque, menus, bandeja) => {
  if (!menus.cc || bandeja !== BANDEJAS.CC) return false;
  const flag = flagAccion(cheque, "puede_pasar_a_cartera");
  if (flag !== null) return flag;
  return cheque?.estado === ESTADOS_WORKFLOW.ACEPTADO;
};

const puedeDefinirDestino = (cheque, menus, bandeja) => {
  if (!menus.cartera || bandeja !== BANDEJAS.CARTERA) return false;
  const flag = flagAccion(cheque, "puede_definir_destino");
  if (flag !== null) return flag;
  return cheque?.estado === ESTADOS_WORKFLOW.DISPONIBLE;
};

/** Alta rechazo bancario: Depositado/Endosado (no confundir con repudiado). */
const puedeAltaRechazo = (cheque, menus, bandeja) => {
  if (!menus.cartera || bandeja !== BANDEJAS.CARTERA) return false;
  const flag =
    flagAccion(cheque, "puede_registrar_rechazo") ??
    flagAccion(cheque, "puede_alta_rechazo");
  if (flag !== null) return flag;
  return (
    cheque?.estado === ESTADOS_WORKFLOW.DEPOSITADO ||
    cheque?.estado === ESTADOS_WORKFLOW.ENDOSADO
  );
};

const tieneAccionesFila = (cheque, menus, bandeja) =>
  puedeRevisarCc(cheque, menus, bandeja) ||
  puedeRepudiar(cheque, menus, bandeja) ||
  puedeBaja(cheque, menus, bandeja) ||
  puedeReabrir(cheque, menus, bandeja) ||
  puedeDefinirDestino(cheque, menus, bandeja) ||
  puedeAltaRechazo(cheque, menus, bandeja);

const sxBtnAccion = {
  minWidth: 0,
  px: 1,
  py: 0.25,
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: "nowrap",
};

const TAM_ACCIONES_VACIO = { size: 52, minSize: 44, maxSize: 56 };
const TAM_ACCIONES_UNO = { size: 92, minSize: 72, maxSize: 110 };
const TAM_ACCIONES_RECHAZO = { size: 152, minSize: 140, maxSize: 172 };

const tamanoColumnaAcciones = (bandeja) => {
  switch (bandeja) {
    case BANDEJAS.FINANZAS:
      return TAM_ACCIONES_VACIO;
    case BANDEJAS.CC:
      return { size: 168, minSize: 120, maxSize: 185 };
    case BANDEJAS.CARTERA:
      return { size: 168, minSize: 120, maxSize: 185 };
    default:
      return { size: 185, minSize: 120, maxSize: 220 };
  }
};

const contarAccionesFila = (cheque, menus, bandeja) =>
  [
    puedeRevisarCc(cheque, menus, bandeja),
    puedeRepudiar(cheque, menus, bandeja),
    puedeBaja(cheque, menus, bandeja),
    puedeReabrir(cheque, menus, bandeja),
    puedeDefinirDestino(cheque, menus, bandeja),
    puedeAltaRechazo(cheque, menus, bandeja),
  ].filter(Boolean).length;

const tamanoColumnaAccionesLista = (bandeja, cheques, menus) => {
  if (bandeja === BANDEJAS.FINANZAS) return TAM_ACCIONES_VACIO;

  const maxAcciones = cheques.reduce(
    (max, cheque) =>
      Math.max(max, contarAccionesFila(cheque, menus, bandeja)),
    0
  );

  if (maxAcciones === 0) return TAM_ACCIONES_VACIO;
  if (maxAcciones === 1) {
    const hayRechazo = cheques.some((cheque) =>
      puedeAltaRechazo(cheque, menus, bandeja)
    );
    return hayRechazo ? TAM_ACCIONES_RECHAZO : TAM_ACCIONES_UNO;
  }
  return tamanoColumnaAcciones(bandeja);
};

export default function ListadoChequesProveedores() {
  const location = useLocation();
  const navigate = useNavigate();
  const bandejaPath = bandejaDesdePath(location.pathname, location.search);

  const [bandeja, setBandeja] = useState(bandejaPath || BANDEJAS.CC);
  const [menus, setMenus] = useState({
    cc: false,
    finanzas: false,
    cartera: false,
  });
  const [menusRechazo, setMenusRechazo] = useState({
    finanzas: false,
    pagos: false,
    jefe: false,
    cc: false,
  });
  const [menuListaNegra, setMenuListaNegra] = useState(false);
  const [cargandoMenus, setCargandoMenus] = useState(true);
  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosIniciales);
  const [page, setPage] = useState(0);
  const [cheques, setCheques] = useState([]);
  const [paginacion, setPaginacion] = useState({
    total: 0,
    limit: PAGE_SIZE,
    offset: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogImportar, setDialogImportar] = useState(false);
  const [dialogImportarAceptados, setDialogImportarAceptados] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState("");
  const [modalRevision, setModalRevision] = useState({
    open: false,
    cheques: [],
    modo: "revisar",
  });
  const [modalBaja, setModalBaja] = useState({ open: false, cheque: null });
  const [modalCartera, setModalCartera] = useState({ open: false, cheque: null });
  const [modalDestino, setModalDestino] = useState({ open: false, cheque: null });
  const [modalAltaRechazo, setModalAltaRechazo] = useState({
    open: false,
    cheque: null,
  });
  const [badges, setBadges] = useState({
    cc_pendientes: 0,
    finanzas_por_aceptar: 0,
    cartera_alerta_30: 0,
    rechazos_finanzas: 0,
    rechazos_pagos: 0,
    rechazos_jefe: 0,
    rechazos_cc: 0,
  });
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  const cargarResumen = useCallback(async () => {
    try {
      const data = await getChequesResumen();
      const b = data?.badges || {};
      setBadges({
        cc_pendientes: Number(b.cc_pendientes ?? 0),
        finanzas_por_aceptar: Number(b.finanzas_por_aceptar ?? 0),
        cartera_alerta_30: Number(b.cartera_alerta_30 ?? 0),
        ...badgesRechazosDesdeResumen(b),
      });
    } catch {
      // badges opcionales: no bloquear listado
    }
  }, []);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      setCargandoMenus(true);
      try {
        const lista = await getMenuesUsuario();
        if (cancelado) return;
        const rutas = new Set(
          (Array.isArray(lista) ? lista : []).map((m) =>
            normalizarRutaMenu(m?.ruta)
          )
        );
        setMenus({
          cc: rutas.has(MENU_CHEQUES.CC),
          finanzas: rutas.has(MENU_CHEQUES.FINANZAS),
          cartera: rutas.has(MENU_CHEQUES.CARTERA),
        });
        setMenusRechazo({
          finanzas: rutas.has(MENU_CHEQUES_RECHAZOS.FINANZAS),
          pagos: rutas.has(MENU_CHEQUES_RECHAZOS.PAGOS),
          jefe: rutas.has(MENU_CHEQUES_RECHAZOS.JEFE),
          cc: rutas.has(MENU_CHEQUES_RECHAZOS.CC),
        });
        setMenuListaNegra(rutas.has(MENU_LISTA_NEGRA));
      } catch {
        if (!cancelado) {
          setMenus({ cc: false, finanzas: false, cartera: false });
          setMenusRechazo({
            finanzas: false,
            pagos: false,
            jefe: false,
            cc: false,
          });
          setMenuListaNegra(false);
        }
      } finally {
        if (!cancelado) setCargandoMenus(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    cargarResumen();
  }, [cargarResumen]);

  const tabsDisponibles = useMemo(() => {
    const tabs = [];
    if (menus.cc) tabs.push(BANDEJAS.CC);
    if (menus.finanzas) tabs.push(BANDEJAS.FINANZAS);
    if (menus.cartera) tabs.push(BANDEJAS.CARTERA);
    return tabs;
  }, [menus]);

  useEffect(() => {
    if (cargandoMenus || !tabsDisponibles.length) return;
    const desdeUrl =
      bandejaPath && tabsDisponibles.includes(bandejaPath) ? bandejaPath : null;
    const siguiente = desdeUrl || (tabsDisponibles.includes(bandeja) ? bandeja : tabsDisponibles[0]);
    if (siguiente !== bandeja) {
      setBandeja(siguiente);
      setPage(0);
      return;
    }
    const destino = rutaEcheq({ vista: VISTA_ECHEQUE.CHEQUES, bandeja: siguiente });
    const actual = `${location.pathname}${location.search}`;
    if (location.pathname.endsWith("/echeq") && actual !== destino) {
      navigate(destino, { replace: true });
    }
  }, [
    cargandoMenus,
    tabsDisponibles,
    bandeja,
    bandejaPath,
    location.pathname,
    location.search,
    navigate,
  ]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getCheques({
        ...filtrosAplicados,
        electronico: 1,
        bandeja,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });

      setCheques(Array.isArray(data.cheques) ? data.cheques : []);
      setPaginacion({
        total: Number(data.total ?? 0),
        limit: Number(data.limit ?? PAGE_SIZE),
        offset: Number(data.offset ?? page * PAGE_SIZE),
      });
    } catch (err) {
      setError(err.message || "Error al cargar cheques");
      setCheques([]);
    } finally {
      setLoading(false);
    }
  }, [filtrosAplicados, page, bandeja]);

  useEffect(() => {
    if (cargandoMenus || !tabsDisponibles.includes(bandeja)) return;
    logEventos(
      `acceso al menú Cheques (${bandeja})`,
      "Proveedores",
      "Cheques",
      localStorage.getItem("user")
    );
  }, [bandeja, cargandoMenus, tabsDisponibles]);

  useEffect(() => {
    if (cargandoMenus || !tabsDisponibles.includes(bandeja)) return;
    cargar();
  }, [cargar, cargandoMenus, tabsDisponibles, bandeja]);

  const aplicarFiltros = (e) => {
    e?.preventDefault?.();
    setPage(0);
    setFiltrosAplicados({ ...filtros });
  };

  const limpiarFiltros = () => {
    const vacio = filtrosIniciales();
    setFiltros(vacio);
    setFiltrosAplicados(vacio);
    setPage(0);
  };

  const cambiarBandeja = (_e, value) => {
    setBandeja(value);
    setPage(0);
    navigate(rutaEcheq({ vista: VISTA_ECHEQUE.CHEQUES, bandeja: value }), {
      replace: true,
    });
  };

  const abrirDetalle = useCallback(async (cheque) => {
    if (!cheque?.id) return;
    setDetalle(cheque);
    setErrorDetalle("");
    setLoadingDetalle(true);
    try {
      const data = await getChequeById(cheque.id, {
        origen_cheque: queryOrigenCheque(cheque),
      });
      if (data?.cheque) setDetalle(data.cheque);
    } catch (err) {
      setErrorDetalle(err.message || "No se pudo cargar el detalle");
    } finally {
      setLoadingDetalle(false);
    }
  }, []);

  const refrescarTrasAccion = () => {
    cargarResumen();
    if (page === 0) cargar();
    else setPage(0);
  };

  const onImportacionOk = (data) => {
    const aviso = data?.aviso_cc;
    const mensaje =
      aviso?.mensaje ||
      (aviso?.pendientes_cc != null
        ? `${aviso.pendientes_cc} cheque(s) listos para revisión en Cuentas Corrientes`
        : "Importación confirmada");
    setToast({
      open: true,
      message: mensaje,
      severity: "success",
    });
    cargarResumen();
    if (page === 0) cargar();
    else setPage(0);
  };

  const onImportacionAceptadosOk = (data) => {
    const matched = Array.isArray(data?.matched) ? data.matched.length : 0;
    const inconsist =
      (Array.isArray(data?.banco_sin_autorizacion_cc)
        ? data.banco_sin_autorizacion_cc.length
        : 0) +
      (Array.isArray(data?.autorizados_sin_match)
        ? data.autorizados_sin_match.length
        : 0);
    setToast({
      open: true,
      message:
        matched > 0
          ? `${matched} cheque(s) pasaron a cartera${
              inconsist ? `. ${inconsist} inconsistencia(s)` : ""
            }`
          : data?.msg || "Conciliación confirmada",
      severity: inconsist ? "warning" : "success",
    });
    cargarResumen();
    if (page === 0) cargar();
    else setPage(0);
  };

  const columns = useMemo(
    () => {
      const tamAcciones = tamanoColumnaAccionesLista(bandeja, cheques, menus);
      return [
      {
        accessorKey: "nro_cheque",
        header: "N° cheque",
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} noWrap>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "id_cheque",
        header: "ID cheque",
        size: 140,
        Cell: ({ cell }) => (
          <Typography variant="body2" noWrap title={cell.getValue()}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "banco_emisor",
        header: "Banco",
        size: 160,
        Cell: ({ cell }) => (
          <Typography variant="body2" noWrap title={cell.getValue()} sx={{ maxWidth: 160 }}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        accessorKey: "importe",
        header: "Importe",
        size: 110,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} noWrap>
            {formatMoneda(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "fecha_pago",
        header: "Fecha pago",
        size: 100,
        Cell: ({ cell }) => formatearFecha(cell.getValue()),
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 148,
        minSize: 140,
        maxSize: 168,
        Cell: ({ cell }) => {
          const label = labelEstado(cell.getValue());
          return (
            <Chip
              label={label}
              title={label}
              size="small"
              color={colorEstado(cell.getValue())}
              variant="outlined"
              sx={{
                maxWidth: "100%",
                height: 24,
                "& .MuiChip-label": {
                  px: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          );
        },
      },
      {
        accessorKey: "origen_cheque",
        header: "Origen",
        size: 72,
        maxSize: 80,
        Cell: ({ row }) => (
          <Chip
            label={abrevOrigenCheque(row.original)}
            title={labelOrigenCheque(row.original)}
            size="small"
            color={colorOrigenCheque(row.original)}
            variant="outlined"
            sx={{ maxWidth: "100%", "& .MuiChip-label": { px: 0.75 } }}
          />
        ),
      },
      {
        accessorKey: "proveedor_razon_social",
        header: "Proveedor",
        size: 180,
        Cell: ({ row }) => {
          const nombre =
            row.original.proveedor_razon_social ||
            row.original.cliente_razon_social ||
            row.original.recibido_de ||
            "—";
          return (
            <Box>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                title={nombre}
                sx={{ maxWidth: 180 }}
              >
                {nombre}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatCuit(row.original.cuit_recibido)}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "destino",
        header: "Destino",
        size: 90,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "dias_desde_emision",
        header: "Días",
        size: 70,
        Cell: ({ cell, row }) => {
          const dias = cell.getValue();
          if (dias == null) return "—";
          const alerta =
            Number(dias) >= 30 &&
            row.original.estado === ESTADOS_WORKFLOW.DISPONIBLE;
          return (
            <Chip
              label={dias}
              size="small"
              color={alerta ? "warning" : "default"}
              variant="outlined"
            />
          );
        },
      },
      {
        id: "acciones",
        header: tamAcciones.size <= 56 ? "Acc." : "Acciones",
        ...tamAcciones,
        enableSorting: false,
        muiTableHeadCellProps: {
          align: tamAcciones.size <= 56 ? "center" : "left",
        },
        muiTableBodyCellProps: {
          align: tamAcciones.size <= 56 ? "center" : "left",
          sx: {
            px: tamAcciones.size <= 56 ? 0.5 : 1,
            overflow: "visible",
          },
        },
        Cell: ({ row }) => {
          const cheque = row.original;
          if (!tieneAccionesFila(cheque, menus, bandeja)) {
            return (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                —
              </Typography>
            );
          }
          return (
            <Stack
              direction="row"
              spacing={0.25}
              flexWrap="wrap"
              useFlexGap
              onClick={(e) => e.stopPropagation()}
              sx={{ maxWidth: tamAcciones.maxSize, overflow: "visible" }}
            >
              {puedeRevisarCc(cheque, menus, bandeja) ? (
                <Button
                  size="small"
                  variant="outlined"
                  sx={sxBtnAccion}
                  onClick={() =>
                    setModalRevision({
                      open: true,
                      cheques: [cheque],
                      modo: "revisar",
                    })
                  }
                >
                  Revisar
                </Button>
              ) : null}
              {puedeRepudiar(cheque, menus, bandeja) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  sx={sxBtnAccion}
                  onClick={() =>
                    setModalRevision({
                      open: true,
                      cheques: [cheque],
                      modo: "repudiar",
                    })
                  }
                >
                  Repudiar
                </Button>
              ) : null}
              {puedeBaja(cheque, menus, bandeja) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  sx={sxBtnAccion}
                  onClick={() => setModalBaja({ open: true, cheque })}
                >
                  Baja
                </Button>
              ) : null}
              {puedeReabrir(cheque, menus, bandeja) ? (
                <Button
                  size="small"
                  variant="outlined"
                  sx={sxBtnAccion}
                  onClick={() =>
                    setModalRevision({
                      open: true,
                      cheques: [cheque],
                      modo: "reabrir",
                    })
                  }
                >
                  Reabrir
                </Button>
              ) : null}
              {puedeDefinirDestino(cheque, menus, bandeja) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  sx={sxBtnAccion}
                  onClick={() =>
                    setModalDestino({ open: true, cheque })
                  }
                >
                  Destino
                </Button>
              ) : null}
              {puedeAltaRechazo(cheque, menus, bandeja) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  sx={sxBtnAccion}
                  title="Registrar rechazo bancario"
                  onClick={() =>
                    setModalAltaRechazo({ open: true, cheque })
                  }
                >
                  Rechazo bancario
                </Button>
              ) : null}
            </Stack>
          );
        },
      },
    ];
    },
    [menus, bandeja, cheques]
  );

  const tieneMenusRechazo = useMemo(
    () =>
      Boolean(
        menusRechazo.finanzas ||
          menusRechazo.pagos ||
          menusRechazo.jefe ||
          menusRechazo.cc
      ),
    [menusRechazo]
  );

  const onAltaRechazoOk = (data, origen) => {
    const destino =
      origen === ORIGEN_RECHAZO.EXTRACTO_BANCARIO
        ? "bandeja Pagos"
        : "bandeja Finanzas (ND)";
    const listaNegra = data?.lista_negra;
    const extraListaNegra = listaNegra?.cuit
      ? ` — CUIT ${listaNegra.cuit} en lista negra`
      : "";
    setToast({
      open: true,
      message:
        data?.msg ||
        `Rechazo registrado — pendiente en ${destino}${extraListaNegra}`,
      severity: "success",
    });
    refrescarTrasAccion();
  };

  // Import solo menú Finanzas (POST /importar). Cartera/CC no importan.
  const puedeImportar =
    Boolean(menus.finanzas) && bandeja === BANDEJAS.FINANZAS;
  const mostrarTabs = tabsDisponibles.length > 0;
  const pendientesRechazo =
    (menusRechazo.finanzas ? badges.rechazos_finanzas : 0) +
    (menusRechazo.pagos ? badges.rechazos_pagos : 0) +
    (menusRechazo.jefe ? badges.rechazos_jefe : 0) +
    (menusRechazo.cc ? badges.rechazos_cc : 0);

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: "20px", pb: 4, px: 2 }}>
        <TabsCircuitoEcheq
          vista={VISTA_ECHEQUE.CHEQUES}
          tieneCheques={tabsDisponibles.length > 0}
          tieneRechazos={tieneMenusRechazo}
          pendientesRechazo={pendientesRechazo}
        />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" fontWeight={700}>
            {tituloBandeja(bandeja)}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {menuListaNegra ? (
              <Button
                component={RouterLink}
                to={rutaListaNegra()}
                variant="outlined"
                size="small"
                color="warning"
              >
                Lista negra
              </Button>
            ) : null}
            {puedeImportar ? (
              <>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setDialogImportar(true)}
                >
                  Importar listado
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setDialogImportarAceptados(true)}
                >
                  Conciliar aceptados
                </Button>
              </>
            ) : null}
          </Stack>
        </Stack>

        {cargandoMenus ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : null}

        {mostrarTabs && tabsDisponibles.includes(bandeja) ? (
          <Tabs
            value={bandeja}
            onChange={cambiarBandeja}
            sx={sxTabsConBadge}
          >
            {tabsDisponibles.includes(BANDEJAS.CC) ? (
              <Tab
                label={labelTabConBadge("Cuentas Corrientes", badges.cc_pendientes)}
                value={BANDEJAS.CC}
              />
            ) : null}
            {tabsDisponibles.includes(BANDEJAS.FINANZAS) ? (
              <Tab
                label={labelTabConBadge("Finanzas", badges.finanzas_por_aceptar)}
                value={BANDEJAS.FINANZAS}
              />
            ) : null}
            {tabsDisponibles.includes(BANDEJAS.CARTERA) ? (
              <Tab
                label={labelTabConBadge("Cartera", badges.cartera_alerta_30)}
                value={BANDEJAS.CARTERA}
              />
            ) : null}
          </Tabs>
        ) : null}

        <Paper
          component="form"
          onSubmit={aplicarFiltros}
          elevation={1}
          sx={{ p: 2, mb: 2, backgroundColor: "#fff" }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <FilterListIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filtros
            </Typography>
          </Stack>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Buscar"
                placeholder="Nro, ID, banco..."
                value={filtros.q}
                onChange={(e) =>
                  setFiltros((prev) => ({ ...prev, q: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Estado"
                value={filtros.estado}
                onChange={(e) =>
                  setFiltros((prev) => ({ ...prev, estado: e.target.value }))
                }
              >
                {ESTADOS_FILTRO.map((opt) => (
                  <MenuItem key={opt.value || "todos"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Origen"
                value={filtros.origen_cheque}
                onChange={(e) =>
                  setFiltros((prev) => ({
                    ...prev,
                    origen_cheque: e.target.value,
                  }))
                }
              >
                {ORIGEN_CHEQUE_FILTRO.map((opt) => (
                  <MenuItem key={opt.value || "todos"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="CUIT recibido"
                value={filtros.cuit_recibido}
                onChange={(e) =>
                  setFiltros((prev) => ({
                    ...prev,
                    cuit_recibido: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Pago desde"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filtros.fecha_pago_desde}
                onChange={(e) =>
                  setFiltros((prev) => ({
                    ...prev,
                    fecha_pago_desde: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Pago hasta"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filtros.fecha_pago_hasta}
                onChange={(e) =>
                  setFiltros((prev) => ({
                    ...prev,
                    fecha_pago_hasta: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" size="small">
                  Buscar
                </Button>
                <Button variant="outlined" size="small" onClick={limpiarFiltros}>
                  Limpiar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={cargar}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <MaterialReactTable
              columns={columns}
              data={cheques}
              layoutMode="grid"
              getRowId={(row) => idFilaCheque(row)}
              localization={MRT_Localization_ES}
              enableColumnActions={false}
              enableColumnFilters={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enablePagination={false}
              initialState={{ density: "compact" }}
              defaultColumn={{ size: 80, minSize: 36 }}
              muiTableBodyRowProps={({ row }) => ({
                onClick: (e) => {
                  if (e.target?.closest?.("button, input")) {
                    return;
                  }
                  abrirDetalle(row.original);
                },
                sx: { cursor: row.original?.id ? "pointer" : "default" },
              })}
              muiTableHeadCellProps={{
                sx: {
                  fontSize: 13,
                  color: "white",
                  fontWeight: "normal",
                  backgroundColor: "#2471A3",
                  whiteSpace: "nowrap",
                  px: 1,
                  border: "none",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  px: 1,
                  py: 1,
                  borderLeft: "none",
                  borderRight: "none",
                  borderTop: "none",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
              muiTableContainerProps={{
                sx: { maxWidth: "100%" },
              }}
              muiTableProps={{
                sx: { tableLayout: "fixed", width: "100%" },
              }}
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                },
              }}
              renderTopToolbarCustomActions={() => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {paginacion.total > 0
                      ? `${paginacion.total} cheque${paginacion.total === 1 ? "" : "s"}`
                      : "Sin resultados"}
                  </Typography>
                  <Button size="small" startIcon={<RefreshIcon />} onClick={cargar}>
                    Actualizar
                  </Button>
                </Stack>
              )}
              renderEmptyRowsFallback={() => (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 3, textAlign: "center" }}
                >
                  No hay cheques con los filtros seleccionados.
                </Typography>
              )}
            />

            <TablePagination
              component="div"
              count={paginacion.total}
              page={page}
              onPageChange={(_e, newPage) => {
                setPage(newPage);
              }}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          </>
        )}
      </Container>

      <ModalImportarCheques
        open={dialogImportar}
        onClose={() => setDialogImportar(false)}
        onImportacionOk={onImportacionOk}
      />

      <ModalImportarAceptados
        open={dialogImportarAceptados}
        onClose={() => setDialogImportarAceptados(false)}
        onImportacionOk={onImportacionAceptadosOk}
      />

      <ModalRevisionCC
        open={modalRevision.open}
        cheques={modalRevision.cheques}
        modo={modalRevision.modo}
        onClose={() =>
          setModalRevision({ open: false, cheques: [], modo: "revisar" })
        }
        onOk={refrescarTrasAccion}
      />

      <ModalBajaCheque
        open={modalBaja.open}
        cheque={modalBaja.cheque}
        onClose={() => setModalBaja({ open: false, cheque: null })}
        onOk={refrescarTrasAccion}
      />

      <ModalPasarACartera
        open={modalCartera.open}
        cheque={modalCartera.cheque}
        onClose={() => setModalCartera({ open: false, cheque: null })}
        onOk={refrescarTrasAccion}
      />

      <ModalDestinoCheque
        open={modalDestino.open}
        cheque={modalDestino.cheque}
        onClose={() => setModalDestino({ open: false, cheque: null })}
        onOk={refrescarTrasAccion}
      />

      <ModalAltaRechazo
        open={modalAltaRechazo.open}
        cheque={modalAltaRechazo.cheque}
        onClose={() => setModalAltaRechazo({ open: false, cheque: null })}
        onOk={onAltaRechazoOk}
      />

      <Dialog
        open={Boolean(detalle)}
        onClose={() => setDetalle(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Detalle del cheque</DialogTitle>
        <DialogContent dividers>
          {loadingDetalle ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {errorDetalle ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {errorDetalle}
                </Alert>
              ) : null}
              <Grid container spacing={1.5}>
                <CampoDetalle label="N° cheque" value={detalle?.nro_cheque} />
                <CampoDetalle label="ID cheque" value={detalle?.id_cheque} />
                <CampoDetalle label="Banco" value={detalle?.banco_emisor} />
                <CampoDetalle
                  label="Importe"
                  value={
                    detalle?.importe != null ? formatMoneda(detalle.importe) : "—"
                  }
                />
                <CampoDetalle
                  label="Fecha emisión"
                  value={formatearFecha(detalle?.fecha_emision)}
                />
                <CampoDetalle
                  label="Fecha pago"
                  value={formatearFecha(detalle?.fecha_pago)}
                />
                <CampoDetalle
                  label="Estado"
                  value={labelEstado(detalle?.estado)}
                />
                <CampoDetalle
                  label="Estado en banco"
                  value={detalle?.estado_banco || "—"}
                />
                <CampoDetalle
                  label="Fecha match banco"
                  value={formatearFecha(detalle?.fecha_match_banco)}
                />
                <CampoDetalle label="Destino" value={detalle?.destino || "—"} />
                <CampoDetalle
                  label="Tipo"
                  value={
                    Number(detalle?.electronico) === 1 ? "E-cheque" : "Físico"
                  }
                />
                <CampoDetalle
                  label="Origen"
                  value={labelOrigenCheque(detalle)}
                />
                {detalle?.id_orden_pago ? (
                  <CampoDetalle
                    label="Orden de pago"
                    value={detalle.id_orden_pago}
                  />
                ) : null}
                <CampoDetalle
                  label="Proveedor"
                  value={
                    detalle?.proveedor_razon_social ||
                    detalle?.cliente_razon_social ||
                    "—"
                  }
                />
                <CampoDetalle
                  label="ID proveedor"
                  value={detalle?.id_proveedor}
                />
                <CampoDetalle label="ID cliente" value={detalle?.id_cliente} />
                <CampoDetalle label="Recibido de" value={detalle?.recibido_de} />
                <CampoDetalle
                  label="CUIT recibido"
                  value={formatCuit(detalle?.cuit_recibido)}
                />
                <CampoDetalle
                  label="Librador"
                  value={detalle?.razon_social_librador}
                />
                <CampoDetalle
                  label="CUIT librador"
                  value={formatCuit(detalle?.cuit_librador)}
                />
                <CampoDetalle label="Obs. CC" value={detalle?.obs_cc} />
                <CampoDetalle
                  label="Obs. Finanzas"
                  value={detalle?.obs_finanzas}
                />
                <CampoDetalle label="User CC" value={detalle?.user_cc} />
                <CampoDetalle
                  label="User Finanzas"
                  value={detalle?.user_finanzas}
                />
                <CampoDetalle
                  label="Fecha baja"
                  value={formatearFecha(detalle?.fecha_baja)}
                />
                <CampoDetalle label="User baja" value={detalle?.user_baja} />
                <CampoDetalle label="ID recibo" value={detalle?.id_recibo} />
                <CampoDetalle
                  label="Días desde emisión"
                  value={detalle?.dias_desde_emision}
                />
                <CampoDetalle label="CMC7" value={detalle?.cmc7} />
                <CampoDetalle label="Cláusula" value={detalle?.clausula} />
                <CampoDetalle
                  label="Archivo origen"
                  value={detalle?.archivo_origen}
                />
                <CampoDetalle label="Usuario import" value={detalle?.user} />
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {detalle && puedePasarCartera(detalle, menus, bandeja) ? (
            <Button
              color="success"
              onClick={() => {
                setModalCartera({ open: true, cheque: detalle });
                setDetalle(null);
              }}
            >
              Pasar a cartera (manual)
            </Button>
          ) : null}
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={8000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
